import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FolderOpen,
  GitBranch,
  Shield,
  Settings,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  TrendingUp,
  Lock,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useProject } from "../contexts/ProjectContext";
import { getTenantFromUrl } from "../utils/enhancedTenantUtils";

const Navigation: React.FC = () => {
  const { user } = useAuth();
  const { currentProject, projects, setCurrentProject } = useProject();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(true);

  const navItems = [
    { to: "/app/dashboard", icon: LayoutDashboard, label: "Project/Documentation Repository" },
    { to: "/app/documents", icon: FolderOpen, label: "Document Ingestion" },
    { to: '/app/sop-builder', icon: FileText, label: 'SOP Builder' },
    { to: "/app/process-maps", icon: GitBranch, label: "Process Maps" },
    { to: "/app/risk-mapping", icon: Shield, label: "Risk Mapping" },
    { to: "/app/process-optimization", icon: Lightbulb, label: "Process Optimization" },
    { to: "/app/executive-dashboard", icon: TrendingUp, label: "Executive Dashboard" },
    // Security Documents - only visible to SUPER_ADMIN
    ...(user?.role === "SUPER_ADMIN" ? [{ to: "/app/security-documents", icon: Lock, label: "Security Documents" }] : []),
    // Show admin for all authenticated users (content filtered by role inside Admin page)
    { to: "/app/admin", icon: Settings, label: user?.role === "SUPER_ADMIN" ? "Admin" : "Settings" },
  ];

  return (
    <nav
      className={`${
        isCollapsed ? "w-20" : "w-64"
      } bg-super-dark-purple text-white h-[calc(100vh-88px)] transition-all duration-300 relative flex flex-col`}
      data-tour="navigation"
    >
      {/* Toggle Button */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 bg-electric-blue text-super-dark-purple rounded-full p-1 hover:bg-opacity-90 transition-colors z-10 shadow-lg"
        title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4" />
        ) : (
          <ChevronLeft className="w-4 h-4" />
        )}
      </button>

      <div className="flex-1 overflow-y-auto bg-super-dark-purple">
        <div className="p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                title={isCollapsed ? item.label : ""}
                data-tour={
                  item.to === "/documents" ? "documents-nav" : undefined
                }
                className={({ isActive }) =>
                  `flex items-center ${
                    isCollapsed ? "justify-center" : "space-x-3"
                  } px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-electric-blue text-super-dark-purple"
                      : "text-gray-300 hover:bg-dusky-purple hover:text-white"
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!isCollapsed && <span>{item.label}</span>}
              </NavLink>
            </li>
          ))}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
